import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import type { CmsFeatureInfo, CmsIpRule, IpRuleType } from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { newestFirst } from "../lib/format";

export function IpRules() {
  const { t, fmtDate } = useI18n();

  // Load My IP info
  const { data: myIpData, reload: reloadMyIp } = useAsync(() => api.ipRules.myIp(), []);
  // Load Available Features
  const { data: featuresData } = useAsync(() => api.ipRules.features(), []);

  // Filter states
  const [filterFeature, setFilterFeature] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");

  // Load Rules with filters
  const {
    data: rulesData,
    loading: rulesLoading,
    error: rulesError,
    reload: reloadRules,
  } = useAsync(
    () =>
      api.ipRules.list({
        feature: filterFeature || undefined,
        ruleType: (filterType as IpRuleType) || undefined,
      }),
    [filterFeature, filterType]
  );

  const [editing, setEditing] = useState<CmsIpRule | "new" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const rules = rulesData ?? [];
  const features = featuresData ?? [];
  const myIp = myIpData;

  async function remove(rule: CmsIpRule) {
    if (!confirm(t("iprules.confirmDelete", { ip: rule.ip }))) return;
    setBusy(`del-${rule.id}`);
    setActionError(null);
    try {
      await api.ipRules.delete(rule.id);
      await reloadRules();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function toggleActive(rule: CmsIpRule) {
    setBusy(`toggle-${rule.id}`);
    setActionError(null);
    try {
      await api.ipRules.update(rule.id, {
        isActive: !rule.isActive,
      });
      await reloadRules();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const columns = useMemo<GridColDef<CmsIpRule>[]>(
    () => [
      {
        field: "feature",
        headerName: t("iprules.feature"),
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            label={row.feature === "all" ? t("iprules.allFeatures") : row.feature}
            color={row.feature === "all" ? "secondary" : "default"}
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        field: "ruleType",
        headerName: t("iprules.ruleType"),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={row.ruleType === "BLOCK" ? "error" : "success"}
            label={row.ruleType === "BLOCK" ? t("iprules.block") : t("iprules.allow")}
            icon={
              row.ruleType === "BLOCK" ? (
                <BlockIcon fontSize="inherit" />
              ) : (
                <CheckCircleOutlinedIcon fontSize="inherit" />
              )
            }
          />
        ),
      },
      {
        field: "ip",
        headerName: t("iprules.ip"),
        flex: 1.2,
        minWidth: 160,
        renderCell: ({ row }) => (
          <span className="mono" style={{ fontWeight: 600, fontSize: 13 }}>
            {row.ip}
          </span>
        ),
      },
      {
        field: "description",
        headerName: t("iprules.description"),
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.description || "—"}
          </Typography>
        ),
      },
      {
        field: "isActive",
        headerName: t("iprules.status"),
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) => (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={row.isActive}
                onChange={() => toggleActive(row)}
                disabled={busy !== null}
              />
            }
            label={
              <Typography variant="caption" color={row.isActive ? "success.main" : "text.secondary"}>
                {row.isActive ? t("iprules.active") : t("iprules.inactive")}
              </Typography>
            }
            sx={{ m: 0 }}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: t("roles.created"),
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="caption" color="text.secondary">
            {fmtDate(row.createdAt)}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 100,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            disabled={busy !== null}
          />,
          <GridActionsCellItem
            key="del"
            icon={<DeleteIcon fontSize="small" />}
            label={t("common.delete")}
            onClick={() => remove(ruleFind(row.id) ?? row)}
            disabled={busy !== null}
          />,
        ],
      },
    ],
    [t, fmtDate, busy, rules]
  );

  function ruleFind(id: number) {
    return rules.find(r => r.id === id);
  }

  return (
    <Stack spacing={2.5}>
      {/* Root IP & Detected IP Banner */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          bgcolor: myIp?.isRootIp ? "rgba(245, 158, 11, 0.08)" : "background.paper",
          borderColor: myIp?.isRootIp ? "warning.main" : "divider",
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          {myIp?.isRootIp ? (
            <VerifiedUserIcon color="warning" sx={{ fontSize: 32 }} />
          ) : (
            <ShieldOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
          )}
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("iprules.myIp")}:
              </Typography>
              <Typography variant="body2" className="mono" sx={{ fontWeight: 700 }}>
                {myIp?.ip ?? "..."}
              </Typography>
              {myIp?.isRootIp && (
                <Tooltip title={t("iprules.rootHint")}>
                  <Chip
                    size="small"
                    color="warning"
                    label={t("iprules.rootBadge")}
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {myIp?.isRootIp ? t("iprules.rootHint") : t("iprules.subtitle")}
            </Typography>
          </Box>
        </Stack>
        <Button variant="default" onClick={reloadMyIp}>
          {t("common.refresh")}
        </Button>
      </Paper>

      {/* Main Rules Card */}
      <Card
        title={t("iprules.title")}
        actions={
          <Button variant="primary" onClick={() => setEditing("new")}>
            + {t("iprules.add")}
          </Button>
        }
      >
        {/* Filters Toolbar */}
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", alignItems: "center", pb: 1 }}>
          <Box sx={{ minWidth: 200 }}>
            <Field label={t("iprules.filterFeature")}>
              <select
                value={filterFeature}
                onChange={e => setFilterFeature(e.target.value)}
                style={{ width: "100%", padding: "6px 10px" }}
              >
                <option value="">— {t("common.all")} —</option>
                <option value="all">{t("iprules.allFeatures")}</option>
                {features.map(f => (
                  <option key={f.key} value={f.key}>
                    {f.name} ({f.key})
                  </option>
                ))}
              </select>
            </Field>
          </Box>

          <Box sx={{ minWidth: 160 }}>
            <Field label={t("iprules.filterType")}>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ width: "100%", padding: "6px 10px" }}
              >
                <option value="">— {t("common.all")} —</option>
                <option value="BLOCK">{t("iprules.block")}</option>
                <option value="ALLOW">{t("iprules.allow")}</option>
              </select>
            </Field>
          </Box>
        </Stack>

        {actionError && <ErrorBox message={actionError} />}
        {rulesError && <ErrorBox message={rulesError} />}

        {rulesLoading && !rulesData ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(rules, "createdAt")}
            columns={columns}
            loading={busy !== null || rulesLoading}
          />
        )}
      </Card>

      {editing && (
        <IpRuleModal
          rule={editing === "new" ? null : editing}
          features={features}
          myIp={myIp?.ip ?? ""}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reloadRules();
          }}
        />
      )}
    </Stack>
  );
}

function IpRuleModal({
  rule,
  features,
  myIp,
  onClose,
  onSaved,
}: {
  rule: CmsIpRule | null;
  features: CmsFeatureInfo[];
  myIp: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [feature, setFeature] = useState(rule?.feature ?? "all");
  const [ruleType, setRuleType] = useState<IpRuleType>(rule?.ruleType ?? "BLOCK");
  const [ip, setIp] = useState(rule?.ip ?? "");
  const [description, setDescription] = useState(rule?.description ?? "");
  const [isActive, setIsActive] = useState(rule?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (rule) {
        await api.ipRules.update(rule.id, {
          feature,
          ruleType,
          ip: ip.trim(),
          description: description.trim(),
          isActive,
        });
      } else {
        await api.ipRules.create({
          feature,
          ruleType,
          ip: ip.trim(),
          description: description.trim(),
          isActive,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={rule ? t("iprules.formEdit") : t("iprules.formNew")}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="form">
        {error && <ErrorBox message={error} />}

        <div className="form-row">
          <Field label={t("iprules.feature")}>
            <select
              value={feature}
              onChange={e => setFeature(e.target.value)}
              required
            >
              <option value="all">{t("iprules.allFeatures")}</option>
              {features.map(f => (
                <option key={f.key} value={f.key}>
                  {f.name} ({f.key})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("iprules.ruleType")}>
            <select
              value={ruleType}
              onChange={e => setRuleType(e.target.value as IpRuleType)}
              required
            >
              <option value="BLOCK">{t("iprules.block")}</option>
              <option value="ALLOW">{t("iprules.allow")}</option>
            </select>
          </Field>
        </div>

        <Field label={t("iprules.ip")} hint={t("iprules.ipHint")}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              value={ip}
              onChange={e => setIp(e.target.value)}
              required
              placeholder="e.g. 192.168.1.100 or 10.0.0.0/24"
              className="mono"
              style={{ flex: 1 }}
            />
            {myIp && (
              <Button
                type="button"
                onClick={() => setIp(myIp)}
                title={t("iprules.useMyIp")}
                style={{ flexShrink: 0 }}
              >
                + {t("iprules.useMyIp")}
              </Button>
            )}
          </div>
        </Field>

        <Field label={t("iprules.description")} hint={t("iprules.descriptionHint")}>
          <input
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t("iprules.commentPlaceholder")}
          />
        </Field>

        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t("iprules.toggleActive")}
            </Typography>
          }
          sx={{ mt: 1 }}
        />

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", mt: 2 }}>
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t("common.save")}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
